import React, { useState, useEffect, useCallback } from "react";
import { Modal, Box, TextField, Button, FormControl, InputLabel, Select, MenuItem, Typography, Tooltip, FormLabel } from "@mui/material";
import { useCollections, sharedCollectionFieldDefaults } from "./hooks";
import { SearchOptionsInput } from "./SearchOptionsInput";
import { NumberField } from "@base-ui-components/react/number-field";
import { COLORS } from "constants/colors";

const defaultFormData = {
  ...sharedCollectionFieldDefaults,
  searchModels: "",
  childGenieCollectionId: ""
};

const ChildCollectionModal = React.memo(({ isOpen, onClose, onSave, child }) => {
  const { data: collections, isLoading } = useCollections();
  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});

  // Update form data when child prop changes or modal opens
  useEffect(() => {
    if (child) {
      const searchOptionsString = child.searchOptions ? JSON.stringify(child.searchOptions) : "";
      setFormData({
        slug: child.slug || "",
        displayName: child.displayName || "",
        overline: child.overline || "",
        description: child.description || "",
        underline: child.underline || "",
        cta: child.cta || "",
        searchOptions: searchOptionsString,
        searchModels: child.searchOptions?.searchableModelsFilter || "",
        childGenieCollectionId: child.childGenieCollectionId ? Number(child.childGenieCollectionId) : "",
        id: child.id ? Number(child.id) : "",
        displayOptions: child.displayOptions || {},
        order: Number(child.order) || 0
      });
      setErrors({});
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData(defaultFormData);
      setErrors({});
    }
  }, [child, isOpen]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    // Convert numeric fields to numbers
    if (name === "childGenieCollectionId" || name === "id" || name === "order") {
      const numValue = value === "" ? "" : Number(value);
      setFormData((prev) => ({ ...prev, [name]: numValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error for the field being changed
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.displayName.trim()) {
      newErrors.displayName = "Display Name is required";
    }

    if (formData.childGenieCollectionId === "") {
      newErrors.childGenieCollectionId = "Child Collection is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSave = useCallback(() => {
    // Validate form before saving
    if (!validateForm()) {
      return;
    }

    const processedFormData = {
      ...formData,
      childGenieCollectionId: formData.childGenieCollectionId === "" ? null : Number(formData.childGenieCollectionId),
      ...(formData.id === "" ? {} : { id: Number(formData.id) }),
      order: Number(formData.order) || 0,
      searchOptions: formData.searchOptions && typeof formData.searchOptions === "string" &&
        formData.searchOptions.trim().startsWith("{")
        ? JSON.parse(formData.searchOptions)
        : formData.searchOptions
    };

    onSave(processedFormData);
    onClose();
  }, [formData, onSave, onClose, validateForm]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          width: "50%",
          height: "100%",
          overflowY: "auto",
          bgcolor: "#f9f9f9",
          padding: 4
        }}
      >
        <h2>{child ? "Edit Child Collection Carousel" : "Add Child Collection Carousel"}</h2>
        <Typography variant="body4" color="text.secondary">Configuration for how this child collection will appear on parent collection page. Step 1: Create a collection. Step 2: Configure it here as a child collection. Note: If child collection has pageContent, it will display in cutout nav bar, otherwise it will display as a search result carousel.</Typography>
        <Tooltip
          title="Determines order in nav item carousel at top of Collection page. Lower numbers are displayed first. If multiple with same order, they will be displayed in order they're added here."
          placement="top"
          arrow
        >
          <NumberField.Root>
            <NumberField.ScrubArea>
              <FormLabel>Order</FormLabel>
              <NumberField.Input
                style={{
                  borderRadius: "4px",
                  padding: "12px",
                  borderColor: COLORS.LIGHT_GRAY
                }}
                name="order"
                onChange={handleChange}
                value={
                  formData.order
                }
              />
            </NumberField.ScrubArea>
          </NumberField.Root>
        </Tooltip>
        <TextField
          fullWidth
          margin="normal"
          label="Slug - optional, defaults to it's general collection slug"
          name="slug"
          value={formData.slug}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Overline - optional"
          name="overline"
          value={formData.overline || ""}
          onChange={handleChange}
          error={!!errors.overline}
          helperText={errors.overline}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Display Name *"
          name="displayName"
          value={formData.displayName}
          onChange={handleChange}
          error={!!errors.displayName}
          helperText={errors.displayName}
          required
        />

        <TextField
          fullWidth
          margin="normal"
          label="Description - optional"
          name="description"
          value={formData.description || ""}
          onChange={handleChange}
          error={!!errors.description}
          helperText={errors.description}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Underline - optional"
          name="underline"
          value={formData.underline || ""}
          onChange={handleChange}
          error={!!errors.underline}
          helperText={errors.underline}
        />
        <TextField
          fullWidth
          margin="normal"
          label="CTA - optional, dictates link to search results page"
          name="cta"
          value={formData.cta || ""}
          onChange={handleChange}
          error={!!errors.cta}
          helperText={errors.cta}
        />
        <Tooltip
          title="These search options will override the default options of the child collection. That is, what you configure here will set how this collection appears on this parent collection page, but not everywhere it is used."
          placement="top"
          arrow
        >
          <SearchOptionsInput
            searchOptions={typeof formData.searchOptions === "string" ? (formData.searchOptions ? JSON.parse(formData.searchOptions) : {}) : formData.searchOptions || {}}
            onChange={(newOptions) =>
              setFormData((prev) => ({
                ...prev,
                searchOptions: JSON.stringify(newOptions)
              }))
            }
          />
        </Tooltip>
        <FormControl fullWidth margin="normal" error={!!errors.childGenieCollectionId} required>
          <InputLabel id="child-collection-select-label">Child Collection *</InputLabel>
          <Select
            labelId="child-collection-select-label"
            id="child-collection-select"
            name="childGenieCollectionId"
            value={formData.childGenieCollectionId}
            label="Child Collection *"
            onChange={handleChange}
            disabled={isLoading}
          >
            {(collections || []).map((collection) => (
              <MenuItem key={collection.id} value={Number(collection.id)}>
                {collection.displayName}
              </MenuItem>
            ))}
          </Select>
          {errors.childGenieCollectionId && (
            <Box component="span" sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5, ml: 1.75 }}>
              {errors.childGenieCollectionId}
            </Box>
          )}
        </FormControl>
        <Box mt={2} display="flex" justifyContent="space-between">
          <Button variant="contained" color="primary" onClick={handleSave}>
            Save
          </Button>
          <Button variant="outlined" color="secondary" onClick={onClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
});

// Add display name to fix the linter warning
ChildCollectionModal.displayName = "ChildCollectionModal";

export default ChildCollectionModal;
